<template>
  <div>
    <div class="table-container" grid-drag-ignore>
      <table class="budget-table">
        <thead class="border-bottom">
          <tr>
            <th v-for="col in sortedHeaders" :key="col.value" :class="getHeaderClass(col)">{{ col.title }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="processedData.length === 0">
            <td :colspan="headers.length" style="text-align: center">No data to display</td>
          </tr>
          <tr v-for="(row, rowIndex) in processedData" :key="rowIndex" :class="{ 'subtotal-row': row.isSubtotal }">
            <template v-for="col in sortedHeaders">
              <td
                v-if="shouldRenderCell(row, col)"
                :key="col.value"
                :rowspan="getCellRowspan(row, col)"
                :class="getCellClass(col, row)"
                @click="onCellClick(rowIndex, col, row, $event)"
              >
                <div class="cell-content">
                  <template v-if="edit && isEditable(col) && isEditing(rowIndex, col)">
                    <input
                      class="table-input-text"
                      :ref="inputRefKey(rowIndex, col)"
                      type="text"
                      v-model="editingValue"
                      inputmode="decimal"
                      @keydown.enter.prevent="commitEdit(rowIndex, col)"
                      @keydown.esc.prevent="cancelEdit"
                      @blur="cancelEdit"
                      @paste.stop.prevent="onPaste($event, col)"
                    />
                  </template>

                  <template v-else>
                    <template v-if="col.value === 'geography'">
                      <span class="geo-text">{{ getDisplayedValue(row, col) }}</span>
                      <ButtonAction
                        v-if="row?.scenario_id"
                        class="markets-btn"
                        no-tooltip
                        icon="mdi-information-outline"
                        @click="$emit('clickPlanLocation', row?.scenario_id)"
                      />
                    </template>
                    <template v-else-if="col.value === 'campaign_kpi'">
                      <span class="geo-text">{{ getDisplayedValue(row, col) }}</span>
                      <v-icon
                        v-if="getErrorText(row, col)"
                        @click="onClickTableKPIAlert"
                        size="large"
                        class="ml-1 mb-1 output-alert-icon"
                        >mdi-alert-circle</v-icon
                      >
                    </template>
                    <template v-else-if="col.value === 'channel'">
                      <span class="geo-text">{{ getDisplayedValue(row, col) }}</span>
                    </template>
                    <template v-else>
                      {{ getDisplayedValue(row, col) }}
                    </template>
                  </template>
                </div>
              </td>
            </template>
          </tr>
        </tbody>
        <tfoot>
          <tr class="subtotal-row">
            <td :colspan="1" class="group-cell">Grand Total</td>
            <template v-for="col in sortedHeaders.slice(1)">
              <td :key="col.value" :class="getFooterCellClass(col)">
                {{ isNumeric(col) ? getGrandTotalValue(col) : "" }}
              </td>
            </template>
          </tr>
        </tfoot>
      </table>
    </div>
    <ChannelIntervalsDrawer
      v-model="channelDrawer.open"
      :row="channelDrawer.row"
      :base-budget="channelDrawer.baseBudget"
      :headers="sortedHeaders"
      @save="onDrawerSave"
    />
    <v-dialog v-model="dateModal.open" width="316">
      <v-card class="project-output-date-modal">
        <v-date-picker
          v-model="dateModal.value"
          :min="dateModal.min"
          :max="dateModal.max"
          no-title
          scrollable
          @input="onDatePicked"
        />
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { cloneDeep } from "lodash";
import Vue, { PropType } from "vue";
import { mapGetters } from "vuex";

import ButtonAction from "@/components/customComponents/ButtonAction.vue";
import ChannelIntervalsDrawer, { DrawerFlightSave } from "@/components/drawers/ChannelIntervalsDrawer.vue";
import { weightedMap } from "@/constants";
import csvExport, { buildFileName, buildHeaderRows } from "@/helpers/csvExport";
import { formatters } from "@/helpers/formatters";
import Lang from "@/lang";
import { ProjectService } from "@/services/projectService";
import planTableMetrics from "@/settings/metrics/planTableMetrics";
import { PlanInputDictionary } from "@/types/common";
import { NUMERIC_KEYS, NumericKey } from "@/types/project";
import {
  BusinessGoal,
  MetricsRowInput,
  ProcessedRow,
  RecalculatePlanInput,
  RowIdentity,
  ScenarioFlightingContext,
} from "@/types/tables";

const masterOrderMap = new Map<string, number>();
planTableMetrics.forEach((metric, index) => {
  masterOrderMap.set(metric.value, index);
});

const UI_TO_API: Record<string, NumericKey> = {
  budget: "flight_budget",
  channel_percentage: "flight_percentage",
};
const VALIDATION = {
  pctTolerance: 0.01,
  budgetTolerance: 1,
};
const PAYLOAD_UI_ONLY_KEYS = new Set([
  "isSubtotal",
  "showBusinessGoal",
  "bgRowspan",
  "showPlan",
  "planRowspan",
  "showScenario",
  "scenarioRowspan",
  "geography",
  "flights",
  "flight_count",
]);
const ALLOWED_METRICS_ROW_KEYS = new Set<string>([
  "id",
  "business_goal",
  "campaign_id",
  "campaign_kpi",
  "plan",
  "channel",
  "flight_budget",
  "media_type",
  "scenario_id",
  "start_date",
  "end_date",
  "run_flighting",
  "flight_percentage",
  "cpm",
  "impressions",
  "cplv",
  "landing_page_views",
  "cpp",
  "purchases",
  "roas",
  "cpa",
  "cpc",
  "ctr",
  "clicks",
  "cpl",
  "cpcv",
  "cpi",
  "cpe",
  "cpae",
  "cpb",
  "cper",
  "cpia",
  "cplc",
  "cppl",
  "cpsa",
  "cpsv",
  "cps",
  "cpla",
  "leads",
  "acquisitions",
  "app_installs",
  "completed_views",
  "engagements",
  "purchase_value",
  "app_events",
  "bookings",
  "event_response",
  "intent_actions",
  "link_clicks",
  "page_likes",
  "site_action",
  "store_visits",
  "submission_entries",
  "video_views",
  "local_action",
  "vcr",
]);
const toInt = (n: number): number => {
  const v = Number(n);
  return Number.isFinite(v) ? Math.trunc(v) : 0;
};

function applyFormatter(col: PlanInputDictionary, val: unknown): unknown {
  if (typeof col.formatter !== "function") return val;

  let fval: string | number | null;
  if (val === undefined || val === null) fval = null;
  else if (typeof val === "number" || typeof val === "string") fval = val;
  else {
    const asNum = Number(val);
    fval = Number.isFinite(asNum) ? asNum : String(val);
  }

  try {
    return col.formatter(fval);
  } catch (e) {
    console.error("[export] formatter error for", col.value, e);
    return val;
  }
}

export default Vue.extend({
  name: "ProjectOutputTable",
  components: { ButtonAction, ChannelIntervalsDrawer },
  props: {
    data: {
      type: Array as PropType<BusinessGoal[]>,
      required: true,
    },
    headers: {
      type: Array as PropType<PlanInputDictionary[]>,
      required: true,
    },
    edit: Boolean,
  },
  data() {
    return {
      editingCell: { rowIndex: -1, colKey: "" as string },
      editingValue: "" as string,
      updateDraft: {
        metrics_rows: [] as MetricsRowInput[],
        index: {} as Record<string, number>,
        scenario_dates_by_id: {} as Record<string, { scenario_id: string; start_date: string; end_date: string }>,
      },
      channelDrawer: {
        open: false,
        row: null as ProcessedRow | null,
        baseBudget: 0,
      },
      dateModal: {
        open: false,
        row: null as ProcessedRow | null,
        field: "" as "start_date" | "end_date" | "",
        value: "",
        min: "",
        max: "",
      },
      validationErrors: new Set<string>(),
    };
  },
  computed: {
    ...mapGetters("projectOutput", ["projectFiltered"]),

    canReset(): boolean {
      return this.hasPendingEdits || this.validationErrors.size > 0 || this.editingCell.rowIndex !== -1;
    },
    hasPendingEdits(): boolean {
      return this.updateDraft.metrics_rows.length > 0;
    },
    isValid(): boolean {
      return this.validationErrors.size === 0;
    },
    canRecalculate(): boolean {
      return this.hasPendingEdits && this.isValid;
    },
    sortedHeaders(): PlanInputDictionary[] {
      const headersCopy = [...this.headers];
      headersCopy.sort((a, b) => {
        const levelDifference = a.lvl - b.lvl;
        if (levelDifference !== 0) {
          return levelDifference;
        }

        const aOriginalIndex = masterOrderMap.get(a.value) ?? Infinity;
        const bOriginalIndex = masterOrderMap.get(b.value) ?? Infinity;

        return aOriginalIndex - bOriginalIndex;
      });
      return headersCopy;
    },
    processedData(): ProcessedRow[] {
      const processedRows: ProcessedRow[] = [];
      if (!this.data || !Array.isArray(this.data)) return [];

      this.data.forEach((bg) => {
        let firstOfBg = true;
        const bgGroupRows: ProcessedRow[] = [];

        bg.children?.forEach((plan) => {
          let firstOfPlan = true;
          const planGroupRows: ProcessedRow[] = [];

          plan.children?.forEach((scenario) => {
            const scenarioGroupRows: ProcessedRow[] = [];

            const groupsByKpi = new Map<string, ProcessedRow[]>();

            scenario.rows.forEach((row) => {
              const kpi = row["campaign_kpi"] || "Other";

              const fullRow: ProcessedRow = {
                ...row,
                business_goal: bg.business_goal,
                campaign_id: bg.campaign_id,
                plan: plan.plan,
                geography: scenario.geography,
                campaign_kpi: kpi,
                scenario_id: scenario.scenario_id,
                start_date: row.start_date,
                end_date: row.end_date,
              };

              if (!groupsByKpi.has(kpi)) {
                groupsByKpi.set(kpi, []);
              }
              groupsByKpi.get(kpi)!.push(fullRow);
            });

            const sortedKpis = Array.from(groupsByKpi.keys()).sort();
            let firstKpiInScenario = true;

            for (const kpi of sortedKpis) {
              const groupRows = groupsByKpi.get(kpi)!;

              groupRows.forEach((row, i) => {
                row.showBusinessGoal = firstOfBg;
                row.showPlan = firstOfPlan;
                row.showScenario = firstKpiInScenario && i === 0;

                firstOfBg = false;
                firstOfPlan = false;
              });

              scenarioGroupRows.push(...groupRows);

              const subtotal: ProcessedRow = { isSubtotal: true };

              this.sortedHeaders.forEach((h) => {
                if (h.lvl !== 2) return;

                if (this.isNumeric(h)) {
                  const values = groupRows.map((r) => Number(r[h.value]) || 0);
                  const localWeightedMap = cloneDeep(weightedMap);
                  const metricInfo = localWeightedMap[h.value];

                  if (metricInfo) {
                    const numSum = groupRows.reduce((sum, r) => sum + (Number(r[metricInfo.num]) || 0), 0);
                    const denomSum = groupRows.reduce((sum, r) => sum + (Number(r[metricInfo.denom]) || 0), 0);
                    subtotal[h.value] =
                      numSum && denomSum
                        ? metricInfo.multiplier
                          ? (metricInfo.multiplier * numSum) / denomSum
                          : numSum / denomSum
                        : 0;
                  } else {
                    subtotal[h.value] = values.reduce((sum, v) => sum + v, 0);
                  }
                } else if (h.value === "campaign_kpi") {
                  subtotal[h.value] = "Subtotal:";
                } else {
                  subtotal[h.value] = "";
                }
              });

              scenarioGroupRows.push(subtotal);
              firstKpiInScenario = false;
            }

            if (scenarioGroupRows.length > 0) {
              scenarioGroupRows[0].scenarioRowspan = scenarioGroupRows.length;
              planGroupRows.push(...scenarioGroupRows);
            }
          });

          if (planGroupRows.length > 0) {
            planGroupRows[0].planRowspan = planGroupRows.length;
            bgGroupRows.push(...planGroupRows);
          }
        });

        if (bgGroupRows.length > 0) {
          bgGroupRows[0].bgRowspan = bgGroupRows.length;
          processedRows.push(...bgGroupRows);
        }
      });

      return processedRows;
    },
  },
  methods: {
    getFlightPatchedValue(id: string, field: string, fallback: unknown): unknown {
      const draft = this.getFlightDraftById(id);
      if (!draft || !Object.prototype.hasOwnProperty.call(draft, field)) {
        return fallback;
      }
      return (draft as Record<string, unknown>)[field];
    },
    openChannelDrawer(row: ProcessedRow) {
      this.channelDrawer = {
        open: true,
        row: this.buildDrawerRow(row),
        baseBudget: this.getGroupOriginalBudgetTotal(row),
      };
    },
    buildDrawerRow(row: ProcessedRow): ProcessedRow {
      const clone = cloneDeep(row) as ProcessedRow & { flights?: Array<Record<string, unknown>> };
      const flights = Array.isArray(clone.flights) ? clone.flights : [];

      clone.flights = flights.map((flight) => {
        const id = String(flight.id ?? "");
        const hydrated = { ...flight } as Record<string, unknown>;

        Object.keys(hydrated).forEach((key) => {
          if (key === "id") return;
          if (key === "budget") {
            hydrated[key] = String(this.getFlightPatchedNumeric(id, "flight_budget", Number(flight.budget ?? 0)));
            return;
          }
          if (key === "channel_percentage") {
            hydrated[key] = String(
              this.getFlightPatchedNumeric(id, "flight_percentage", Number(flight.channel_percentage ?? 0))
            );
            return;
          }
          hydrated[key] = String(this.getFlightPatchedValue(id, key, flight[key]));
        });

        return hydrated;
      });

      clone.channel_budget = this.getRowNumericValue(row, "flight_budget", true);
      clone.channel_percentage = this.getRowNumericValue(row, "flight_percentage", true);
      clone.budget = clone.channel_budget;
      return clone;
    },
    getFlightDraftById(id: string): MetricsRowInput | undefined {
      if (!id) return undefined;
      const idx = this.updateDraft.index[id];
      return idx !== undefined ? this.updateDraft.metrics_rows[idx] : undefined;
    },
    getFlightPatchedNumeric(id: string, field: "flight_budget" | "flight_percentage", fallback: number): number {
      const draft = this.getFlightDraftById(id);
      if (!draft || !Object.prototype.hasOwnProperty.call(draft, field)) {
        return field === "flight_budget" ? toInt(fallback) : fallback;
      }

      const val = Number((draft as Record<string, unknown>)[field]);
      if (!Number.isFinite(val)) return field === "flight_budget" ? toInt(fallback) : fallback;
      return field === "flight_budget" ? toInt(val) : val;
    },
    hasPatchedFlights(row: ProcessedRow, field: string): boolean {
      const flights = Array.isArray((row as Record<string, unknown>).flights)
        ? ((row as Record<string, unknown>).flights as unknown[]) ?? []
        : [];
      return flights.some((flight) => {
        const id = String((flight as Record<string, unknown>).id ?? "");
        const draft = this.getFlightDraftById(id);
        return !!draft && Object.prototype.hasOwnProperty.call(draft, field);
      });
    },
    getRowNumericValue(row: ProcessedRow, field: "flight_budget" | "flight_percentage", usePatched: boolean): number {
      if (Number(row.flight_count ?? 1) > 1) {
        const flights = Array.isArray((row as Record<string, unknown>).flights)
          ? ((row as Record<string, unknown>).flights as unknown[]) ?? []
          : [];
        const summed = flights.reduce<number>((acc, flight) => {
          const item = flight as Record<string, unknown>;
          const id = String(item.id ?? "");
          const fallback = Number(field === "flight_budget" ? item.budget ?? 0 : item.channel_percentage ?? 0);
          const value: number = usePatched ? this.getFlightPatchedNumeric(id, field, fallback) : fallback;
          return acc + (field === "flight_budget" ? toInt(value) : value);
        }, 0);
        return field === "flight_budget" ? toInt(summed) : summed;
      }

      return this.getSingleFlightNumericValue(row, field, usePatched);
    },
    aggregateFlightMetricValue(row: ProcessedRow, field: string, usePatched: boolean): number {
      const flights = Array.isArray((row as Record<string, unknown>).flights)
        ? ((row as Record<string, unknown>).flights as unknown[]) ?? []
        : [];
      if (!flights.length) return 0;

      const header = this.sortedHeaders.find((item) => String(item.value) === field);
      const values = flights.map((flight) => {
        const item = flight as Record<string, unknown>;
        const id = String(item.id ?? "");
        const raw = usePatched ? this.getFlightPatchedValue(id, field, item[field]) : item[field];
        const numeric = Number(raw);
        return Number.isFinite(numeric) ? numeric : 0;
      });

      if (header?.tableGroup === "calculated" || header?.type === "average") {
        return values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : 0;
      }

      return values.reduce((sum, value) => sum + value, 0);
    },
    getAggregatedFlightDate(row: ProcessedRow, field: "start_date" | "end_date", usePatched: boolean): string {
      const flights = Array.isArray((row as Record<string, unknown>).flights)
        ? ((row as Record<string, unknown>).flights as unknown[]) ?? []
        : [];
      const values = flights
        .map((flight) => {
          const item = flight as Record<string, unknown>;
          const id = String(item.id ?? "");
          const raw = usePatched ? this.getFlightPatchedValue(id, field, item[field]) : item[field];
          return String(raw ?? "");
        })
        .filter(Boolean)
        .sort();

      if (!values.length) return "";
      return field === "start_date" ? values[0] : values[values.length - 1];
    },
    getGroupBudgetTotal(base: ProcessedRow, usePatched: boolean): number {
      const peers = this.getGroupRows(base);
      return peers.reduce((acc, row) => acc + this.getRowNumericValue(row, "flight_budget", usePatched), 0);
    },
    onDrawerSave(flights: DrawerFlightSave[]) {
      const channelRow = this.channelDrawer.row;
      if (!channelRow) return;

      const sid = String(channelRow.scenario_id ?? "");
      if (sid && !this.updateDraft.scenario_dates_by_id[sid]) {
        this.$set(this.updateDraft.scenario_dates_by_id, sid, {
          scenario_id: sid,
          start_date: String(channelRow.start_date ?? ""),
          end_date: String(channelRow.end_date ?? ""),
        });
      }

      for (const flight of flights) {
        const key = flight.id;
        let idx = this.updateDraft.index[key];
        if (idx === undefined) {
          idx = this.updateDraft.metrics_rows.length;
          this.updateDraft.index[key] = idx;
          const nextDraft: MetricsRowInput = {
            business_goal: String(channelRow.business_goal ?? ""),
            campaign_id: String(channelRow.campaign_id ?? ""),
            campaign_kpi: String(channelRow.campaign_kpi ?? ""),
            plan: String(channelRow.plan ?? ""),
            channel: String(channelRow.channel ?? ""),
            media_type: String(channelRow.media_type ?? ""),
            scenario_id: sid,
            id: flight.id,
            flight_budget: flight.flight_budget,
            flight_percentage: flight.flight_percentage,
            start_date: flight.start_date,
            end_date: flight.end_date,
          };
          Object.entries(flight).forEach(([metricKey, metricValue]) => {
            if (["id", "flight_budget", "flight_percentage", "start_date", "end_date"].includes(metricKey)) return;
            const asNumber = Number(metricValue);
            nextDraft[metricKey] = Number.isFinite(asNumber) ? asNumber : String(metricValue ?? "");
          });
          this.updateDraft.metrics_rows.push(nextDraft);
        } else {
          const draft = { ...this.updateDraft.metrics_rows[idx] };
          draft.id = flight.id;
          draft.flight_budget = flight.flight_budget;
          draft.flight_percentage = flight.flight_percentage;
          draft.start_date = flight.start_date;
          draft.end_date = flight.end_date;
          Object.entries(flight).forEach(([metricKey, metricValue]) => {
            if (["id", "flight_budget", "flight_percentage", "start_date", "end_date"].includes(metricKey)) return;
            const asNumber = Number(metricValue);
            draft[metricKey] = Number.isFinite(asNumber) ? asNumber : String(metricValue ?? "");
          });
          this.$set(this.updateDraft.metrics_rows, idx, draft);
        }
      }

      console.log("[ProjectOutputTable:onDrawerSave]", {
        channel: channelRow.channel,
        campaign_kpi: channelRow.campaign_kpi,
        flights,
        updateDraft: this.updateDraft.metrics_rows,
      });

      this.validateGroupTotals(channelRow);
    },
    onClickTableKPIAlert() {
      this.$store.dispatch("confirmDialog/open", {
        title: Lang.budgetAllocationErrorTitle,
        message: Lang.budgetAllocationError,
        cancelText: Lang.ok,
        confirmText: "",
      });
    },
    sanitizePastedNumeric(raw: string, dropFraction = false): string {
      let s = raw.trim().replace(/[^\d.,-]+/g, "");

      const lastComma = s.lastIndexOf(",");
      const lastDot = s.lastIndexOf(".");
      const decIdx = Math.max(lastComma, lastDot);

      let intPart = s;
      let decPart = "";

      if (decIdx > -1) {
        intPart = s.slice(0, decIdx);
        decPart = s.slice(decIdx + 1);
      }

      intPart = intPart.replace(/[.,]/g, "");

      if (dropFraction) {
        return intPart.replace(/^0+(?=\d)/, "") || "0";
      }

      decPart = decPart.replace(/[^\d]/g, "");
      const numStr = decPart ? `${intPart}.${decPart}` : intPart;
      return numStr.replace(/^0+(?=\d)/, "") || "0";
    },

    onPaste(e: ClipboardEvent, col: PlanInputDictionary) {
      const text = (e.clipboardData?.getData("text") ?? "").trim();

      if (!this.isNumeric(col)) {
        this.editingValue = text;
        return;
      }
      const isBudget = col.value === "budget" || UI_TO_API[col.value] === "channel_budget";
      this.editingValue = this.sanitizePastedNumeric(text, isBudget);
    },
    buildExportMatrix(): unknown[][] {
      const cols = this.sortedHeaders;
      const keys = this.getExportKeys();

      const out: unknown[][] = [this.getExportHeaderRow()];

      for (const row of this.processedData) {
        const csvRow: unknown[] = [];

        this.sortedHeaders.forEach((col, i) => {
          const uiKey = String(col.value);
          const dataKey = keys[i];

          if (row.isSubtotal) {
            if (col.lvl !== 2) {
              csvRow.push("");
              return;
            }
            const raw = row[dataKey] ?? row[uiKey] ?? "";
            const outVal = this.isNumeric(col) ? applyFormatter(col, raw) : raw;
            csvRow.push(outVal);
            return;
          }

          if (col.lvl === 0 && !row.showBusinessGoal) {
            csvRow.push("");
            return;
          }
          if (col.lvl === 1) {
            if (uiKey === "plan" && !row.showPlan) {
              csvRow.push("");
              return;
            }
            if (uiKey === "geography" && !row.showScenario) {
              csvRow.push("");
              return;
            }
          }

          let val: unknown;
          if (this.isNumeric(col) && col.lvl === 2) {
            val = this.getPatchedNumeric(row, dataKey as NumericKey);
          } else {
            val = row[dataKey] ?? row[uiKey] ?? "";
          }

          if (this.isNumeric(col)) {
            val = applyFormatter(col, val);
          }

          csvRow.push(val);
        });

        out.push(csvRow);
      }

      if (this.processedData.length) {
        const totalRow: unknown[] = [];
        cols.forEach((col, idx) => {
          if (idx === 0) {
            totalRow.push("Grand Total");
            return;
          }
          totalRow.push(this.isNumeric(col) ? this.getGrandTotalValue(col) : "");
        });
        out.push(totalRow);
      }

      return out;
    },

    exportProcessedData(): void {
      const matrix = this.buildExportMatrix();

      const title = "Projected Recommendations";

      const csvContent: unknown[][] = [];
      csvContent.push(...buildHeaderRows(title));
      csvContent.push([]);
      csvContent.push(...matrix);

      const fileName = buildFileName(title);
      csvExport(csvContent, fileName);
    },
    getExportKeys(): string[] {
      return this.sortedHeaders.map((h) => UI_TO_API[String(h.value)] ?? String(h.value));
    },

    getExportHeaderRow(): string[] {
      return this.sortedHeaders.map((h) => String(h.title));
    },
    isDateField(col: PlanInputDictionary): boolean {
      return col.value === "start_date" || col.value === "end_date";
    },
    getRawEditableValue(row: ProcessedRow, col: PlanInputDictionary): unknown {
      const id = this.makeIdentity(row);
      const key = this.identityKey(id);
      const idx = this.updateDraft.index[key];
      const draftRow = idx !== undefined ? this.updateDraft.metrics_rows[idx] : undefined;

      const dataKey = (UI_TO_API[col.value] ?? col.value) as string;

      if (draftRow && Object.prototype.hasOwnProperty.call(draftRow, dataKey)) {
        return (draftRow as Record<string, unknown>)[dataKey];
      }
      return row[dataKey];
    },
    resetDraft() {
      this.cancelEdit();
      this.closeDateModal();
      this.validationErrors = new Set<string>();
      this.$set(this, "updateDraft", {
        metrics_rows: [],
        index: {},
        scenario_dates_by_id: {},
      });
    },
    getGroupOriginalBudgetTotal(base: ProcessedRow): number {
      return this.getGroupBudgetTotal(base, false);
    },

    validateGroupTotals(changedRow: ProcessedRow) {
      const newErrors = new Set<string>();
      const groups = new Map<string, ProcessedRow[]>();

      this.processedData.forEach((row) => {
        if (row.isSubtotal) return;
        const key = `${String(row.scenario_id ?? "")}::${String(row.campaign_kpi ?? "")}`;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key)!.push(row);
      });

      for (const peers of groups.values()) {
        if (this.hasAnyPatched(peers, "flight_percentage")) {
          const sumPct = peers.reduce((acc, row) => acc + this.getRowNumericValue(row, "flight_percentage", true), 0);
          const okPct = Math.abs(sumPct - 100) <= VALIDATION.pctTolerance;
          if (!okPct) {
            peers.forEach((r) => {
              newErrors.add(this.cellValidationKey(r, "flight_percentage"));
            });
          }
        }

        if (this.hasAnyPatched(peers, "flight_budget")) {
          const baseRow = peers[0];
          const orig = this.getGroupBudgetTotal(baseRow, false);
          const patched = this.getGroupBudgetTotal(baseRow, true);
          const okBudget = Math.abs(patched - orig) <= VALIDATION.budgetTolerance;
          if (!okBudget) {
            peers.forEach((r) => {
              newErrors.add(this.cellValidationKey(r, "flight_budget"));
            });
          }
        }
      }

      this.validationErrors = newErrors;
    },

    getDraftIndexByRow(row: ProcessedRow): number | undefined {
      const id = this.makeIdentity(row);
      const key = this.identityKey(id);
      return this.updateDraft.index[key];
    },
    ensureDraftRow(row: ProcessedRow): number {
      const id = this.makeIdentity(row);
      const key = this.identityKey(id);

      let idx = this.updateDraft.index[key];
      if (idx !== undefined) return idx;

      idx = this.updateDraft.metrics_rows.length;
      this.updateDraft.index[key] = idx;
      this.updateDraft.metrics_rows.push({ ...id, id: String(row.id ?? "") });

      const sid = id.scenario_id;
      if (sid && !this.updateDraft.scenario_dates_by_id[sid]) {
        this.$set(this.updateDraft.scenario_dates_by_id, sid, {
          scenario_id: sid,
          start_date: String(row.start_date ?? ""),
          end_date: String(row.end_date ?? ""),
        });
      }

      return idx;
    },

    getDraftRowByRow(row: ProcessedRow): MetricsRowInput | undefined {
      const idx = this.getDraftIndexByRow(row);
      return idx !== undefined ? this.updateDraft.metrics_rows[idx] : undefined;
    },

    getPatchedNumeric(row: ProcessedRow, field: NumericKey): number {
      const draft = this.getDraftRowByRow(row);
      const patched =
        draft && Object.prototype.hasOwnProperty.call(draft, field)
          ? Number((draft as Record<string, unknown>)[field])
          : undefined;
      const original = Number((row as Record<string, unknown>)[field]);
      const v = patched !== undefined ? patched : original;

      if (field === "channel_budget" || field === "flight_budget") {
        return toInt(v);
      }
      return Number.isFinite(v) ? Number(v) : 0;
    },
    getSingleFlightNumericValue(row: ProcessedRow, field: NumericKey, usePatched: boolean): number {
      if (!usePatched) {
        const original = Number((row as Record<string, unknown>)[field]);
        if (field === "channel_budget" || field === "flight_budget") {
          return toInt(original);
        }
        return Number.isFinite(original) ? Number(original) : 0;
      }

      return this.getPatchedNumeric(row, field);
    },

    getGroupRows(base: ProcessedRow): ProcessedRow[] {
      return this.processedData.filter(
        (r) =>
          !r.isSubtotal &&
          String(r.scenario_id) === String(base.scenario_id) &&
          String(r.campaign_kpi) === String(base.campaign_kpi)
      );
    },

    cellValidationKey(row: ProcessedRow, field: string) {
      const id = this.identityKey(this.makeIdentity(row));
      return `${id}::${field}`;
    },

    hasAnyPatched(peers: ProcessedRow[], field: NumericKey): boolean {
      return peers.some((r) => {
        if ((field === "flight_budget" || field === "flight_percentage") && Number(r.flight_count ?? 1) > 1) {
          return this.hasPatchedFlights(r, field);
        }
        const draft = this.getDraftRowByRow(r);
        return !!draft && Object.prototype.hasOwnProperty.call(draft, field);
      });
    },

    sumGroup(peers: ProcessedRow[], field: NumericKey, usePatched: boolean): number {
      return peers.reduce((acc, r) => {
        let v = usePatched ? this.getPatchedNumeric(r, field) : Number(r[field]) || 0;
        if (field === "channel_budget" || field === "flight_budget") v = toInt(v);
        return acc + (Number.isFinite(v) ? v : 0);
      }, 0);
    },
    isEditedCell(row: ProcessedRow, col: PlanInputDictionary): boolean {
      if (row.isSubtotal) return false;
      if (col.lvl !== 2) return false;

      const field = (UI_TO_API[col.value] ?? col.value) as string;
      if (Number(row.flight_count ?? 1) > 1) {
        if (!this.hasPatchedFlights(row, field)) return false;
        if (field === "start_date" || field === "end_date") {
          return this.getAggregatedFlightDate(row, field, true) !== this.getAggregatedFlightDate(row, field, false);
        }
        if (field === "flight_budget" || field === "flight_percentage") {
          return this.getRowNumericValue(row, field, true) !== this.getRowNumericValue(row, field, false);
        }
        return this.aggregateFlightMetricValue(row, field, true) !== this.aggregateFlightMetricValue(row, field, false);
      }

      const id = this.makeIdentity(row);
      const key = this.identityKey(id);
      const idx = this.updateDraft.index[key];
      if (idx === undefined) return false;

      const draftRow = this.updateDraft.metrics_rows[idx];
      const hasPatched = Object.prototype.hasOwnProperty.call(draftRow, field);
      if (!hasPatched) return false;

      if (field === "start_date" || field === "end_date") {
        return String((draftRow as Record<string, unknown>)[field] ?? "") !== String(row[field] ?? "");
      }

      if (!NUMERIC_KEYS.has(field as NumericKey)) return false;

      const original = Number(row[field]) || 0;
      const patched = Number((draftRow as Record<string, unknown>)[field]);
      if (Number.isNaN(patched)) return false;

      return patched !== original;
    },
    getCellClass(col: PlanInputDictionary, row?: ProcessedRow): Record<string, boolean> {
      if (!row) return {};

      const isEdited = this.isEditedCell(row, col);
      const key = this.cellValidationKey(row, UI_TO_API[col.value] ?? col.value);
      const hasError = this.validationErrors.has(key);

      return {
        "group-cell": col.lvl < 2,
        "text-right": this.isNumeric(col),
        "border-right": col.lvl < 2,
        "border-bottom": col.lvl <= 2,
        "edited-cell": isEdited && !hasError,
        "error-cell": hasError,
      };
    },

    findSourceRow(id: RowIdentity & { id?: string }): ProcessedRow | undefined {
      if (id.id) {
        const byFlightId = this.processedData.find(
          (pr: ProcessedRow) => !pr.isSubtotal && String(pr.id ?? "") === String(id.id)
        );
        if (byFlightId) return byFlightId;
      }

      return this.processedData.find(
        (pr: ProcessedRow) =>
          !pr.isSubtotal &&
          String(pr.business_goal) === id.business_goal &&
          String(pr.campaign_id) === id.campaign_id &&
          String(pr.campaign_kpi) === id.campaign_kpi &&
          String(pr.plan) === id.plan &&
          String(pr.channel) === id.channel &&
          String(pr.media_type ?? "") === id.media_type &&
          String(pr.scenario_id ?? "") === id.scenario_id
      );
    },
    toPayloadMetricsRow(row: Record<string, unknown>): MetricsRowInput {
      const payloadRow: MetricsRowInput = {} as MetricsRowInput;

      Object.entries(row).forEach(([key, value]) => {
        if (PAYLOAD_UI_ONLY_KEYS.has(key)) return;
        if (value === undefined || typeof value === "string" || typeof value === "number") {
          payloadRow[key] = value;
        }
      });

      return payloadRow;
    },

    makeIdentity(row: ProcessedRow): RowIdentity {
      return {
        business_goal: String(row.business_goal ?? ""),
        campaign_id: String(row.campaign_id ?? ""),
        campaign_kpi: String(row.campaign_kpi ?? ""),
        plan: String(row.plan ?? ""),
        channel: String(row.channel ?? ""),
        media_type: String(row.media_type ?? ""),
        scenario_id: String(row.scenario_id ?? ""),
      };
    },
    identityKey(id: RowIdentity): string {
      const entries: [keyof RowIdentity, string][] = [
        ["business_goal", id.business_goal],
        ["campaign_id", id.campaign_id],
        ["campaign_kpi", id.campaign_kpi],
        ["plan", id.plan],
        ["channel", id.channel],
        ["media_type", id.media_type],
        ["scenario_id", id.scenario_id],
      ];
      return entries.map(([k, v]) => `${k}=${encodeURIComponent(v ?? "")}`).join("&");
    },

    applyEdit(row: ProcessedRow, field: string, valueRaw: string) {
      const target = (UI_TO_API[field] ?? field) as string;
      if (!NUMERIC_KEYS.has(target as NumericKey)) return;

      let value = Number(valueRaw);
      if (!Number.isFinite(value)) return;

      const idx = this.ensureDraftRow(row);

      const round2 = (n: number) => Math.round(n * 100) / 100;

      const TOTAL = toInt(this.getGroupOriginalBudgetTotal(row));
      const draft = { ...this.updateDraft.metrics_rows[idx] };

      if (target === "channel_percentage" || target === "flight_percentage") {
        value = Math.max(0, Math.min(100, value));
        draft.flight_percentage = round2(value);

        draft.flight_budget = TOTAL > 0 ? toInt((value / 100) * TOTAL) : 0;
      } else if (target === "channel_budget" || target === "flight_budget") {
        value = Math.max(0, value);
        const budgetInt = toInt(value);
        draft.flight_budget = budgetInt;

        const newPct = TOTAL > 0 ? (budgetInt / TOTAL) * 100 : 0;
        draft.flight_percentage = round2(newPct);
      } else {
        (draft as Record<string, unknown>)[target] = value;
      }

      this.$set(this.updateDraft.metrics_rows, idx, draft);
    },
    openDateModal(row: ProcessedRow, col: PlanInputDictionary) {
      const draft = this.getDraftRowByRow(row);
      const field = col.value as "start_date" | "end_date";
      const projectService = new ProjectService(this.projectFiltered);
      const scenario = projectService.getScenarioById(String(row.scenario_id ?? ""));

      this.dateModal = {
        open: true,
        row,
        field,
        value: String((draft as Record<string, unknown> | undefined)?.[field] ?? row[field] ?? ""),
        min: String(scenario?.start_date ?? ""),
        max: String(scenario?.end_date ?? ""),
      };
    },
    closeDateModal() {
      this.dateModal = {
        open: false,
        row: null,
        field: "",
        value: "",
        min: "",
        max: "",
      };
    },
    onDatePicked(value: string) {
      const row = this.dateModal.row;
      const field = this.dateModal.field;
      if (!row || !field) return;

      const idx = this.ensureDraftRow(row);
      const draft = { ...this.updateDraft.metrics_rows[idx] };
      draft[field] = value || "";
      this.$set(this.updateDraft.metrics_rows, idx, draft);

      this.closeDateModal();
    },
    websterRoundSingle(value: number, allValues: number[]): number {
      if (!Array.isArray(allValues) || allValues.length === 0) return 0;

      const targetTotal = Math.round(allValues.reduce((a, b) => a + (Number(b) || 0), 0));

      if (targetTotal <= 0) return 0;

      const entries: { score: number; index: number }[] = [];

      allValues.forEach((vRaw, i) => {
        const v = Number(vRaw);
        if (!isFinite(v) || v < 0) return;

        for (let k = 0; k < targetTotal; k++) {
          const divisor = 2 * k + 1;
          entries.push({ score: v / divisor, index: i });
        }
      });

      entries.sort((a, b) => b.score - a.score);

      const result = new Array(allValues.length).fill(0);
      let used = 0;

      for (let i = 0; i < entries.length && used < targetTotal; i++) {
        const idx = entries[i].index;
        result[idx]++;
        used++;
      }

      let targetIndex = -1;
      for (let i = 0; i < allValues.length; i++) {
        if (allValues[i] === value) {
          targetIndex = i;
          break;
        }
      }

      if (targetIndex === -1) return 0;

      return result[targetIndex];
    },

    getErrorText(row: ProcessedRow, col: PlanInputDictionary) {
      if (col.value !== "campaign_kpi" || row?.isSubtotal) return;
      return this.pickStatusSummary(row);
    },

    pickStatusSummary(row: ProcessedRow) {
      const projectService = new ProjectService(this.projectFiltered);
      const sc = projectService.getScenarioById(row.scenario_id);
      const missingKpis = sc?.output?.kpis_with_no_sample;

      return this.checkMissingSample(missingKpis, row.campaign_kpi);
    },
    checkMissingSample(kpisWithoutSample: string[] | null | undefined, targetKpi: string): string | null {
      if (!Array.isArray(kpisWithoutSample)) {
        return null;
      }

      const normalizedTarget = typeof targetKpi === "string" ? targetKpi.trim().toLowerCase() : "";
      if (!normalizedTarget) {
        return null;
      }

      const missingSet = new Set(
        kpisWithoutSample.filter((kpi): kpi is string => typeof kpi === "string").map((kpi) => kpi.trim().toLowerCase())
      );

      if (!missingSet.has(normalizedTarget)) return null;

      return "Sample for KPI/Channel combination not found. Assign budget to channel in Input tab and generate to get results.";
    },
    getDisplayedValue(row: ProcessedRow, col: PlanInputDictionary) {
      const id = this.makeIdentity(row);
      const key = this.identityKey(id);
      const idx = this.updateDraft.index[key];
      const draftRow = idx !== undefined ? this.updateDraft.metrics_rows[idx] : undefined;

      const displayKey = (UI_TO_API[col.value] ?? col.value) as string;
      const patched =
        draftRow && displayKey in draftRow ? (draftRow as Record<string, unknown>)[displayKey] : undefined;

      let raw = patched !== undefined ? patched : row[col.value];
      const flights = Array.isArray((row as Record<string, unknown>).flights)
        ? ((row as Record<string, unknown>).flights as Array<Record<string, unknown>>)
        : [];
      if (Number(row.flight_count ?? 1) > 1 && (displayKey === "flight_budget" || displayKey === "flight_percentage")) {
        raw = this.getRowNumericValue(row, displayKey, true);
      } else if (Number(row.flight_count ?? 1) > 1 && (displayKey === "start_date" || displayKey === "end_date")) {
        raw = this.getAggregatedFlightDate(row, displayKey, true);
      } else if (
        Number(row.flight_count ?? 1) > 1 &&
        displayKey !== "start_date" &&
        displayKey !== "end_date" &&
        flights.length > 0 &&
        Object.prototype.hasOwnProperty.call(flights[0], displayKey)
      ) {
        raw = this.aggregateFlightMetricValue(row, displayKey, true);
      }

      if (!row.isSubtotal && displayKey === "flight_percentage" && !this.edit) {
        const groupRows = this.processedData.filter(
          (r) => !r.isSubtotal && r.scenario_id === row.scenario_id && r.campaign_kpi === row.campaign_kpi
        );

        const percentages = groupRows.map((r) => Number(r.channel_percentage ?? 0));

        const roundedValue = this.websterRoundSingle(row[col.value], percentages);
        return formatters.compactPercentFormat(roundedValue);
      } else if (row.isSubtotal && displayKey === "flight_percentage" && !this.edit) {
        return formatters.integerPercentDirectFormat(raw);
      }

      if (this.edit && displayKey === "flight_percentage") {
        return typeof raw === "number" ? raw.toFixed(2).replace(/\.?0+$/, "") : raw ?? "";
      }

      if (col.formatter) {
        try {
          return this.isNumeric(col) ? col.formatter(raw, { notation: "compact" }) : col.formatter(raw);
        } catch (e) {
          console.error(e);
        }
      }

      return raw;
    },

    buildScenarioFlightingContexts(scenarioIds: string[]): ScenarioFlightingContext[] {
      const projectService = new ProjectService(this.projectFiltered);
      const advertiserName = String(this.projectFiltered?.advertiser_profile?.name ?? "");

      return scenarioIds.map((scenarioId) => {
        const scenario = projectService.getScenarioById(scenarioId);
        const businessGoal = projectService.getBusinessGoalByScenarioId(scenarioId);
        const profileType = String(businessGoal?.b2b_or_b2c ?? "");
        const profile =
          profileType.toUpperCase() === "B2B"
            ? this.projectFiltered?.advertiser_profile?.b2b
            : this.projectFiltered?.advertiser_profile?.b2c;

        return {
          scenario_id: scenarioId,
          gender: String(scenario?.gender ?? ""),
          age_group: String(scenario?.age_group ?? ""),
          dma_names: Array.isArray(scenario?.dma) ? (scenario?.dma as string[]) : [],
          advertiser_name: advertiserName,
          profile_type: profileType,
          brand_recognition: Number(profile?.brand_recognition ?? 0),
          estimated_time_to_conversion: Number(profile?.estimated_time_to_conversion ?? 0),
        };
      });
    },

    buildFinalPayload(): RecalculatePlanInput {
      const scenario_dates = Object.values(this.updateDraft.scenario_dates_by_id);

      const metrics_rows = this.updateDraft.metrics_rows.map((patch) => {
        const src = this.findSourceRow(patch);
        const merged: MetricsRowInput = {
          ...(src ? this.toPayloadMetricsRow(src as Record<string, unknown>) : {}),
          ...patch,
          id: String(patch.id ?? src?.id ?? ""),
          flight_budget:
            patch.flight_budget !== undefined
              ? patch.flight_budget
              : Number(src?.flight_budget ?? src?.channel_budget ?? src?.budget ?? 0),
          flight_percentage:
            patch.flight_percentage !== undefined
              ? patch.flight_percentage
              : Number(src?.flight_percentage ?? src?.channel_percentage ?? 0),
          start_date: patch.start_date !== undefined ? patch.start_date : String(src?.start_date ?? ""),
          end_date: patch.end_date !== undefined ? patch.end_date : String(src?.end_date ?? ""),
          run_flighting: Number(src?.flight_count ?? 1) > 1,
        };

        const row: MetricsRowInput = {} as MetricsRowInput;
        for (const [key, value] of Object.entries(merged)) {
          if (ALLOWED_METRICS_ROW_KEYS.has(key)) {
            row[key] = value;
          }
        }

        return row;
      });

      const scenario_flighting_contexts = this.buildScenarioFlightingContexts(scenario_dates.map((d) => d.scenario_id));

      console.log("[buildFinalPayload] metrics_rows:", metrics_rows);
      return { scenario_dates, scenario_flighting_contexts, metrics_rows };
    },

    async onClickPreparePayload() {
      const payload = this.buildFinalPayload();
      const errors = this.validateUpdatePayload(payload);

      if (errors.length) {
        console.error("[/update_plan] payload validation failed:", errors);
        return;
      }

      try {
        await this.$store.dispatch("projectOutput/recalculatePlan", payload);

        this.resetDraft();
      } catch (e) {
        console.error(e);
      }
    },
    onClickResetEdits() {
      this.resetDraft();
    },

    validateUpdatePayload(payload: RecalculatePlanInput): string[] {
      const errs: string[] = [];

      if (!payload.metrics_rows || payload.metrics_rows.length === 0) {
        errs.push("metrics_rows is empty.");
      }

      for (const [i, row] of payload.metrics_rows.entries()) {
        if (!row.scenario_id) {
          errs.push(`metrics_rows[${i}]: missing scenario_id`);
        }
        const requiredIds: (keyof MetricsRowInput)[] = [
          "business_goal",
          "campaign_id",
          "campaign_kpi",
          "plan",
          "channel",
          // "media_type",
        ];
        for (const k of requiredIds) {
          if (!String(row[k] ?? "").length) {
            errs.push(`metrics_rows[${i}]: field ${k} is empty`);
          }
        }
      }

      const haveDates = new Map(payload.scenario_dates.map((d) => [d.scenario_id, d]));
      const haveContexts = new Map(payload.scenario_flighting_contexts.map((c) => [c.scenario_id, c]));
      const neededIds = new Set(payload.metrics_rows.map((r) => r.scenario_id));
      for (const sid of neededIds) {
        if (!sid) continue;
        if (!haveDates.has(sid)) {
          errs.push(`scenario_dates: no record for scenario_id=${sid}`);
        }
        if (!haveContexts.has(sid)) {
          errs.push(`scenario_flighting_contexts: no record for scenario_id=${sid}`);
        }
      }

      return errs;
    },

    isEditable(col: PlanInputDictionary): boolean {
      return !!col.editable;
    },
    inputRefKey(rowIndex: number, col: PlanInputDictionary) {
      return `edit:${rowIndex}:${col.value}`;
    },
    cellKey(rowIndex: number, col: PlanInputDictionary) {
      return `${rowIndex}:${col.value}`;
    },
    isEditing(rowIndex: number, col: PlanInputDictionary) {
      return this.editingCell.rowIndex === rowIndex && this.editingCell.colKey === col.value;
    },
    getFlightCount(row: ProcessedRow): number {
      return Number(row.flight_count ?? 1);
    },
    onCellClick(rowIndex: number, col: PlanInputDictionary, row: ProcessedRow, e: MouseEvent) {
      if (row.isSubtotal) return;
      if (!(this.edit && this.isEditable(col))) return;

      e.stopPropagation();

      if (this.getFlightCount(row) > 1) {
        this.cancelEdit();
        this.openChannelDrawer(row);
        return;
      }

      if (this.isDateField(col)) {
        this.cancelEdit();
        this.openDateModal(row, col);
        return;
      }

      this.editingCell = { rowIndex, colKey: col.value as string };

      const raw = this.getRawEditableValue(row, col);

      if (this.isNumeric(col)) {
        const num = Number(raw);
        if (col.value === "budget" || UI_TO_API[col.value] === "channel_budget") {
          this.editingValue = Number.isFinite(num) ? String(toInt(num)) : "";
        } else {
          this.editingValue = Number.isFinite(num) ? num.toFixed(2) : "";
        }
      } else {
        this.editingValue = String(raw ?? "");
      }

      this.$nextTick(() => {
        const ref = this.$refs[this.inputRefKey(rowIndex, col)] as HTMLInputElement | HTMLInputElement[] | undefined;
        const el = Array.isArray(ref) ? ref[0] : ref;
        el?.focus();
        el?.select();
      });
    },
    commitEdit(rowIndex: number, col: PlanInputDictionary) {
      const row = this.processedData[rowIndex];
      if (!row || row.isSubtotal) {
        this.cancelEdit();
        return;
      }

      this.applyEdit(row, String(col.value), this.editingValue);

      this.validateGroupTotals(row);

      this.editingCell = { rowIndex: -1, colKey: "" };
      this.editingValue = "";
    },

    cancelEdit() {
      this.editingCell = { rowIndex: -1, colKey: "" };
      this.editingValue = "";
    },
    getGrandTotalValue(col: PlanInputDictionary): string | number {
      const dataRows = this.processedData.filter((r) => !r.isSubtotal);

      if (col.value === "campaign_kpi") {
        return "Grand Total:";
      }

      if (col.value === "channel_percentage") {
        return "";
      }

      if (!this.isNumeric(col)) {
        return "";
      }

      const localWeightedMap = cloneDeep(weightedMap);
      const metricInfo = localWeightedMap[col.value];

      let result: number;

      if (metricInfo) {
        const numSum = dataRows.reduce((sum, r) => sum + (Number(r[metricInfo.num]) || 0), 0);
        const denomSum = dataRows.reduce((sum, r) => sum + (Number(r[metricInfo.denom]) || 0), 0);

        result =
          numSum && denomSum
            ? metricInfo.multiplier
              ? (metricInfo.multiplier * numSum) / denomSum
              : numSum / denomSum
            : 0;
      } else {
        const values = dataRows.map((r) => Number(r[col.value]) || 0);
        result = values.reduce((sum, v) => sum + v, 0);
      }

      return col.formatter ? col.formatter(result, { notation: "compact" }) : result;
    },
    shouldRenderCell(row: ProcessedRow, col: PlanInputDictionary): boolean {
      if (row.isSubtotal) {
        return col.lvl === 2;
      }
      if (col.lvl === 0) return !!row.showBusinessGoal;
      if (col.lvl === 1) {
        if (col.value === "plan") return !!row.showPlan;
        if (col.value === "geography") return !!row.showScenario;
      }
      return col.lvl === 2;
    },
    getCellRowspan(row: ProcessedRow, col: PlanInputDictionary): number {
      if (row.isSubtotal) return 1;
      if (col.lvl === 0) return row.bgRowspan || 1;
      if (col.value === "plan") return row.planRowspan || 1;
      if (col.value === "geography") return row.scenarioRowspan || 1;
      return 1;
    },

    getFooterCellClass(col: PlanInputDictionary): Record<string, boolean> {
      return {
        "group-cell": col.lvl < 2,
        "text-right": this.isNumeric(col),
        "border-bottom": col.lvl <= 2,
      };
    },
    getHeaderClass(col: PlanInputDictionary): Record<string, boolean> {
      return {
        "text-right": this.isNumeric(col),
      };
    },
    getCellValue(row: ProcessedRow, col: PlanInputDictionary): string | number {
      const value = row[col.value];
      if (col.formatter) {
        return col.formatter(value, { notation: "compact" });
      }
      return value;
    },
    isNumeric(col: PlanInputDictionary): boolean {
      return col.tableGroup === "standard" || col.tableGroup === "calculated";
    },
  },
  watch: {
    canRecalculate: {
      immediate: true,
      handler(val: boolean) {
        this.$emit("canRecalculateChange", val);
      },
    },
    canReset: {
      immediate: true,
      handler(val: boolean) {
        this.$emit("canResetChange", val);
      },
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/scss/custom_variables";

.table-container {
  overflow-x: auto;
}
.budget-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  border-bottom: 1px solid #ffffff14;
}
.budget-table th,
.budget-table td {
  padding: 8px;
  text-align: left;
  white-space: nowrap;
  vertical-align: middle;
}
.budget-table th {
  font-weight: 600;
  top: 0;
}
.text-right {
  text-align: right;
}
.group-cell {
  font-weight: 500;
  vertical-align: top;
}
.border-right {
  border-right: 1px solid #ffffff14;
}

.border-bottom {
  border-bottom: 1px solid #ffffff14;
}

.subtotal-row {
  font-weight: bold;
  background: var(--v-background-medium-base) !important;

  &:hover {
    background: var(--v-background-medium-base) !important;
  }
}
.cell-content {
  position: relative;
  width: 100%;
}

.table-input-text {
  background: #151728;
  z-index: 9999;
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 75px !important;
  max-width: 75px !important;
  box-sizing: border-box;
  color: white !important;
}

.error-cell {
  background: transparent !important;
  outline: 2px solid #ff4d4f;
  outline-offset: -4px;
  border-radius: 8px;
}

.edited-cell {
  background: rgba(124, 58, 237, 0.16) !important;
  outline: 2px solid var(--v-accent-global-base);
  outline-offset: -4px;
  border-radius: 8px;
}

.v-application.theme--dark {
  .data-table {
    tr:hover:not(.v-data-table__expanded__content):not(.v-data-table__empty-wrapper):not(.subtotal-row) {
      background-color: unset !important;
    }
  }
}
.channel-edit-btn {
  ::v-deep .v-icon {
    color: rgba(255, 255, 255, 0.3) !important;
  }
  &:hover ::v-deep .v-icon {
    color: rgba(255, 255, 255, 0.7) !important;
  }
}
.markets-btn {
  ::v-deep .v-icon {
    color: #62e9a952 !important;
  }
}
::v-deep .v-btn--icon.v-size--default {
  height: initial !important;
  width: initial !important;
  margin-bottom: 2px;
}

.output-alert-icon {
  opacity: 0.7;
  color: var(--v-error-base) !important;
}

.project-output-date-modal {
  background: var(--v-background-light-base);
  padding: 12px;
}

::v-deep .v-picker__body {
  background-color: initial !important;
}
</style>
